/**
 * What the editors draw inside a room's walls. The walls always show; the
 * built-ins (flooring, counters, interior walls, built-in objects) and the
 * movable objects (furniture and appliances) each turn on and off.
 */
import { type FloorPlanFreeElement, freeElementType, retypesFloor } from '@kaiizen/kai-floor-plan';

export interface PlanLayers {
  builtIns: boolean;
  objects: boolean;
}

export const ALL_PLAN_LAYERS: PlanLayers = { builtIns: true, objects: true };

/** The layers the view menus offer, in menu order, with their labels. The walls always show. */
export const PLAN_LAYER_CHOICES: readonly (readonly [layer: keyof PlanLayers, label: string])[] = [
  ['builtIns', 'Built-ins'],
  ['objects', 'Furniture and appliances'],
];

const storedLayer = (stored: Partial<Record<keyof PlanLayers, unknown>>, layer: keyof PlanLayers): boolean => {
  const value = stored[layer];
  return typeof value === 'boolean' ? value : ALL_PLAN_LAYERS[layer];
};

/** Layers read back from a saved preference: a layer missing or unreadable shows. */
export const parsePlanLayers = (stored: unknown): PlanLayers => {
  const record = (stored && typeof stored === 'object' ? stored : {}) as Partial<Record<keyof PlanLayers, unknown>>;
  return { builtIns: storedLayer(record, 'builtIns'), objects: storedLayer(record, 'objects') };
};

/**
 * In the built-ins layer: flooring, and whatever its type's rule builds in
 * (counters and interior walls always, an object unless it is movable). An
 * element type this version doesn't know shows with the objects.
 */
export const isBuiltInElement = (element: FloorPlanFreeElement): boolean => retypesFloor(element) || freeElementType(element).builtIn(element);

/** The layer `element` shows with. */
export const elementLayer = (element: FloorPlanFreeElement): keyof PlanLayers => isBuiltInElement(element) ? 'builtIns' : 'objects';

export const showsElement = (layers: PlanLayers, element: FloorPlanFreeElement): boolean => layers[elementLayer(element)];

/** The room's typed floors show with the built-ins. */
export const showsFloors = (layers: PlanLayers): boolean => layers.builtIns;

/** Whether anything inside the walls shows at all. */
export const showsInterior = (layers: PlanLayers): boolean => layers.builtIns || layers.objects;

/** The room with only the interior elements these layers show, for exports. */
export const roomWithLayers = <Room extends { freeElements?: FloorPlanFreeElement[] }>(room: Room, layers: PlanLayers): Room =>
  layers.builtIns && layers.objects ? room : { ...room, freeElements: (room.freeElements ?? []).filter(element => showsElement(layers, element)) };
